import { useEffect, useState } from "react";
import React from "react";

import { Button, CreateFolderDialog, Headline, SkeletonLoader, UserType } from "@fronterahealth/frontera-ui-components";

interface EmptyStateProps {
  /**
   * The main header text to display in the empty state view. This typically provides a summary or primary message to the user.
   */
  header?: string;

  /**
   * The subheader text to provide additional context or information beneath the main header. This often explains why the view is empty or offers more details.
   */
  subHeader?: string;

  /**
   * The text for the action button in the empty state view. This button usually provides an option for the user to take action, such as creating new content. Example: "+ create new folder", "+ add new"
   */
  buttonText?: string;
}

export interface RecommendationBankPanelProps {
  /**
   * An array of React nodes representing the actual goals to be displayed in the panel.
   */
  recommendationsToDisplay: React.ReactNode[];

  /**
   * The properties for rendering the empty state message when there are no goals or folders to display.
   */
  emptyState: EmptyStateProps;

  /**
   * A boolean indicating whether the panel is currently empty, meaning there are no goals or folders to show. Used to conditionally render the empty state view.
   */
  isEmpty: boolean;

  isLoading: boolean;

  /**
   * Render an add new button. This will be displayed when there are no goals and when there are goals already saved.
   * Adjust the button styles depending if there are goals or not.
   */
  addNewButton: () => React.ReactNode;

  /**
   * A callback function to create a new folder. Is called when a user submits the folder creation dialog.
   *
   * @param folderName - The name of the folder to create.
   * @param folderType - The type of the folder to create, such as "personal" or "organizational".
   */
  onCreateFolder: (folderName: string, folderType: string) => void;

  /**
   * A required prop in order to determine which UI experience for user type.
   */
  userType: UserType;

  /**
   * Optional bulk-selection bar shown above the cards: a "Select all" toggle for the cards on display and a count of
   * the selected recs Add would insert, including ones in other folders. Omit it for panels that don't select cards.
   */
  selection?: {
    selectedCount: number;
    visibleCount: number;
    allVisibleSelected: boolean;
    onToggleAll: () => void;
  };
}

export const RecommendationsBankPanel: React.FC<RecommendationBankPanelProps> = ({
  emptyState,
  recommendationsToDisplay,
  isEmpty,
  isLoading,
  onCreateFolder,
  addNewButton,
  userType,
  selection,
}) => {
  const hasRecommendations = !!recommendationsToDisplay?.length;
  const addNewButtonContent = addNewButton();
  const hasAddNewButton = React.isValidElement(addNewButtonContent) && addNewButtonContent.type !== React.Fragment;

  const [openNewFolderDialog, setNewFolderDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    isLoading = true;
  }, []);

  if (isLoading) {
    return (
      <div className="h-full w-full bg-limestone-50 p-4">
        <SkeletonLoader loading={true}>
          <div className="h-60 w-60"></div>
        </SkeletonLoader>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="h-full w-full bg-limestone-50 p-4">
        <div className="flex flex-col items-center gap-4 rounded-md border-2 border-limestone-200 p-4">
          <div className="flex flex-col items-center">
            <Headline size="medium">{emptyState?.header}</Headline>
            <span className="text-sm font-normal leading-5 text-text-secondary">{emptyState?.subHeader}</span>
          </div>
          {emptyState.buttonText && (
            <Button
              appearance="primary"
              buttonAction="action"
              text={emptyState.buttonText}
              onClick={() => setNewFolderDialogOpen(true)}
            />
          )}
          <CreateFolderDialog
            open={openNewFolderDialog}
            setOpen={setNewFolderDialogOpen}
            userType={userType}
            onCreateFolder={(folderName, folderType) => {
              onCreateFolder(folderName, folderType);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {selection && (
        <div className="flex items-center justify-between border-b border-limestone-200 bg-white px-4 py-2">
          {selection.visibleCount > 0 && (
            <button
              type="button"
              aria-pressed={selection.allVisibleSelected}
              className="text-sm font-medium text-purple-700 hover:underline"
              onClick={selection.onToggleAll}
            >
              {selection.allVisibleSelected ? "Clear all" : "Select all"}
            </button>
          )}
          <span
            data-testid="recommendations-selected-count"
            className="ml-auto rounded-full bg-limestone-100 px-2 py-0.5 text-xs font-medium text-text-secondary"
          >
            {selection.selectedCount} selected
          </span>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-auto bg-limestone-50">
        {hasRecommendations ? (
          <div className="grid auto-rows-max grid-cols-3 gap-4 p-4">
            {addNewButtonContent}
            {recommendationsToDisplay}
          </div>
        ) : (
          <div className="p-4">
            <div className="flex flex-col items-center gap-4 rounded-md border-2 border-limestone-200 p-4">
              <div className="flex flex-col items-center">
                <Headline size="medium">Nothing Here Yet</Headline>
                {hasAddNewButton && (
                  <span className="text-sm font-normal leading-5 text-text-secondary">
                    Click "Add New" to begin organizing.
                  </span>
                )}
              </div>
              {addNewButtonContent}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
